import { useMemo, useState } from "react"
import { Line, LineChart, Legend, ReferenceLine, Tooltip, XAxis, YAxis, ResponsiveContainer } from "recharts"
import { Text } from "@/components"
import { CapTableFunding, CompanyEmployeeCount, EmployeeCountData } from "@/types"
import { Box, HStack, Stack, VStack } from "@chakra-ui/react"
import { ChartRangeSelector } from "./ChartRangeSelector"
import { filterBeforeFoundingDate, formatGrowth, Timeframe, TIMEFRAME_OPTIONS } from "./chartUtils"
import { useFundraisingMarkers } from "./FundraisingMarkers"

interface Props {
  employeeCounts: CompanyEmployeeCount[]
  salesCount: EmployeeCountData[]
  engineeringCount: EmployeeCountData[]
  capTableFunding?: CapTableFunding[]
  foundingDate?: string | null
}

type GrowthResult = {
  total: number
  sales: number
  engineering: number
}

function calculateGrowth(
  employeeCounts: CompanyEmployeeCount[],
  salesCountData: EmployeeCountData[],
  engineeringCountData: EmployeeCountData[],
  days: number
): GrowthResult | null {
  if (employeeCounts.length < 2) return null

  const sorted = [...employeeCounts]
    .map((d) => ({ ...d, dateObj: new Date(d.date) }))
    .sort((a, b) => a.dateObj.getTime() - b.dateObj.getTime())

  const latestDate = new Date(sorted[sorted.length - 1].scrapeDate || sorted[sorted.length - 1].date)
  const cutoff = new Date(latestDate)
  cutoff.setDate(cutoff.getDate() - days)

  const ecWindow = sorted.filter((d) => d.dateObj >= cutoff && d.dateObj <= latestDate)
  const totalGrowth = ecWindow.length >= 2 && ecWindow[0].count !== 0
    ? (ecWindow[ecWindow.length - 1].count - ecWindow[0].count) / ecWindow[0].count
    : 0

  const calcSeriesGrowth = (data: EmployeeCountData[]) => {
    const s = [...data]
      .map((d) => ({ ...d, dateObj: new Date(d.date) }))
      .sort((a, b) => a.dateObj.getTime() - b.dateObj.getTime())
      .filter((d) => d.dateObj >= cutoff && d.dateObj <= latestDate)
    if (s.length < 2 || s[0].count === 0) return 0
    return (s[s.length - 1].count - s[0].count) / s[0].count
  }

  return {
    total: totalGrowth,
    sales: calcSeriesGrowth(salesCountData),
    engineering: calcSeriesGrowth(engineeringCountData),
  }
}

export const HeadCountChart = ({ employeeCounts, salesCount, engineeringCount, capTableFunding = [], foundingDate }: Props) => {
  const [timeframe, setTimeframe] = useState<Timeframe>("180day")

  const selectedDays = TIMEFRAME_OPTIONS.find((t) => t.value === timeframe)!.days

  const chartData = useMemo(() => {
    const dateMap = new Map<string, { date: string; total?: number; sales?: number; engineering?: number; marketing?: number; operations?: number }>()

    employeeCounts.forEach((d) => {
      const existing = dateMap.get(d.date) || { date: d.date }
      existing.total = d.count
      existing.marketing = d.marketingCount ?? undefined
      existing.operations = d.operationsCount ?? undefined
      dateMap.set(d.date, existing)
    })

    salesCount.forEach((d) => {
      const existing = dateMap.get(d.date) || { date: d.date }
      existing.sales = d.count
      dateMap.set(d.date, existing)
    })

    engineeringCount.forEach((d) => {
      const existing = dateMap.get(d.date) || { date: d.date }
      existing.engineering = d.count
      dateMap.set(d.date, existing)
    })

    const sorted = Array.from(dateMap.values()).sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    )
    return filterBeforeFoundingDate(sorted, foundingDate)
  }, [employeeCounts, salesCount, engineeringCount, foundingDate])

  const growth = useMemo(() => {
    return calculateGrowth(employeeCounts, salesCount, engineeringCount, selectedDays)
  }, [chartData, selectedDays])

  const fundraisingMarkers = useFundraisingMarkers(capTableFunding, chartData.map((d) => d.date))

  const totalGrowth = growth ? formatGrowth(growth.total) : null
  const salesGrowth = growth ? formatGrowth(growth.sales) : null
  const engineeringGrowth = growth ? formatGrowth(growth.engineering) : null

  if (chartData.length === 0) {
    return <Text color="text.muted">No head count data available.</Text>
  }

  return (
    <Box w="100%" p={5} border="1px" borderColor="gray.200" borderRadius={10} boxShadow="sm" bg="card.bg">
      <ChartRangeSelector options={TIMEFRAME_OPTIONS} value={timeframe} onChange={setTimeframe} />

      <HStack alignItems="flex-start" gap={6} mt={5}>
        <VStack align="flex-start" minW="160px" gap={4} pt={2}>
          <Stack gap={0}>
            <Text fontSize="xs" color="text.muted" fontWeight="medium">
              {TIMEFRAME_OPTIONS.find((t) => t.value === timeframe)!.label} Growth
            </Text>
            <Text fontSize="3xl" fontWeight="bold" color={totalGrowth?.color || "gray.600"}>
              {totalGrowth ? totalGrowth.text : "N/A"}
            </Text>
          </Stack>

          {growth && (
            <>
              <Stack gap={0}>
                <Text fontSize="xs" color="text.muted" fontWeight="medium">
                  Sales Growth
                </Text>
                <Text fontSize="lg" fontWeight="semibold" color={salesGrowth?.color || "gray.600"}>
                  {salesGrowth ? salesGrowth.text : "N/A"}
                </Text>
              </Stack>

              <Stack gap={0}>
                <Text fontSize="xs" color="text.muted" fontWeight="medium">
                  Engineering Growth
                </Text>
                <Text fontSize="lg" fontWeight="semibold" color={engineeringGrowth?.color || "gray.600"}>
                  {engineeringGrowth ? engineeringGrowth.text : "N/A"}
                </Text>
              </Stack>
            </>
          )}
        </VStack>

        <Box flex={1}>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <XAxis dataKey="date" fontSize={12} />
              <YAxis allowDecimals={false} fontSize={12} />
              <Tooltip contentStyle={{ backgroundColor: "var(--chakra-colors-card-bg)" }} />
              <Legend />
              {fundraisingMarkers.map((m, i) => (
                <ReferenceLine key={`fr-${i}`} x={m.x} stroke="#A0AEC0" strokeDasharray="4 4" label={{ value: m.label, position: "top", fontSize: 10, fill: "#718096" }} />
              ))}
              <Line type="monotone" dataKey="total" name="Total" stroke="#38A169" dot={false} connectNulls />
              <Line type="monotone" dataKey="sales" name="Sales" stroke="#3182CE" dot={false} connectNulls />
              <Line type="monotone" dataKey="engineering" name="Engineering" stroke="#D69E2E" dot={false} connectNulls />
              <Line type="monotone" dataKey="marketing" name="Marketing" stroke="#805AD5" dot={false} connectNulls />
              <Line type="monotone" dataKey="operations" name="Operations" stroke="#DD6B20" dot={false} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </HStack>
    </Box>
  )
}
